import { Indicator, Root } from '@radix-ui/react-checkbox';
import { ComponentProps, ReactNode, useRef } from 'react';

import { mergeClasses } from '@/utils';

import { CheckIcon, MinusIcon } from '@/assets/icons';
import { Label, SvgIcon } from '@/components/common';

import { CHECKBOX_VARIANTS, ICON_VARIANTS, LABEL_VARIANTS } from './Checkbox.constants';

interface ICheckbox extends ComponentProps<typeof Root> {
  label?: string;
  error?: string;
  labelClassName?: string;
  size?: 'default' | 'lg';
}

export const Checkbox = ({
  className,
  error,
  label,
  disabled,
  labelClassName,
  size,
  ...props
}: ICheckbox) => {
  const checkboxRef = useRef<HTMLButtonElement>(null);
  return (
    <div className="inline-flex flex-col">
      <div className="flex items-center gap-2">
        <Root
          data-slot="checkbox"
          className={mergeClasses(CHECKBOX_VARIANTS({ size, error: !!error, disabled }), className)}
          {...props}
          ref={checkboxRef}
          disabled={disabled}
        >
          <Indicator
            data-slot="checkbox-indicator"
            className="grid place-content-center text-current transition-none"
          >
            <SvgIcon
              component={props.checked === 'indeterminate' ? MinusIcon : CheckIcon}
              className={ICON_VARIANTS({ size, disabled })}
            />
          </Indicator>
        </Root>
        {label && (
          <Label
            className={mergeClasses(LABEL_VARIANTS({ size }), labelClassName)}
            onClick={() => checkboxRef.current?.click()}
          >
            {label}
          </Label>
        )}
      </div>
      {error && <p className="text-xs leading-5 text-semantic-error-base mt-1">{error}</p>}
    </div>
  );
};

interface ICardCheckbox extends ComponentProps<typeof Root> {
  children: ReactNode;
  error?: string;
  indicatorClassName?: string;
}

export const CardCheckbox = ({
  className,
  error,
  children,
  disabled,
  indicatorClassName,
  ...props
}: ICardCheckbox) => {
  return (
    <div className="inline-flex flex-col">
      <div className="relative">
        <Root
          data-slot="checkbox"
          data-variant="card"
          className={mergeClasses(
            'relative w-fit flex flex-col items-end justify-start rounded-xs border transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-neutral-10 dark:border-neutral-8 dark:text-neutral-0',
            // Default
            'bg-neutral-0 border-neutral-4 text-neutral-9',
            // Selected
            'data-[state=checked]:bg-primary-lightest data-[state=checked]:border-primary-base data-[state=checked]:text-primary-base dark:data-[state=checked]:bg-primary-darkest dark:data-[state=checked]:border-primary-base dark:data-[state=checked]:text-neutral-0',
            // Disabled
            disabled &&
              'cursor-not-allowed !bg-neutral-2 !border-neutral-4 !text-neutral-6 dark:!bg-neutral-9 dark:!border-neutral-8 dark:!text-neutral-6',
            // Disabled + Selected
            disabled &&
              'data-[state=checked]:!bg-neutral-2 data-[state=checked]:!border-neutral-4 data-[state=checked]:!text-neutral-6',
            // Error
            error &&
              'bg-neutral-0 border-semantic-error-base dark:!bg-neutral-10 dark:!border-semantic-error-base',
            className,
          )}
          {...props}
          disabled={disabled}
        >
          <Indicator
            data-slot="checkbox-indicator"
            className={mergeClasses(
              'absolute top-2 right-2 size-4 rounded-full border-2 bg-neutral-0 border-neutral-4 grid place-content-center text-current transition-colors',
              // Selected
              'data-[state=checked]:bg-primary-base data-[state=checked]:border-primary-base data-[state=checked]:text-white',
              // Disabled
              disabled &&
                'data-[state=unchecked]:bg-neutral-2 data-[state=unchecked]:border-neutral-4 dark:data-[state=unchecked]:bg-neutral-8 dark:data-[state=unchecked]:border-neutral-6',
              // Disabled + Selected
              disabled &&
                'data-[state=checked]:!bg-neutral-6 data-[state=checked]:!border-neutral-6 data-[state=checked]:!text-neutral-0 dark:data-[state=checked]:!bg-neutral-8 dark:data-[state=checked]:!border-neutral-8 dark:data-[state=checked]:!text-neutral-6',
              indicatorClassName,
            )}
          >
            <SvgIcon
              component={props.checked === 'indeterminate' ? MinusIcon : CheckIcon}
              className="size-3"
            />
          </Indicator>
          {children}
        </Root>
      </div>
      {error && <p className="text-xs leading-5 text-semantic-error-base mt-1">{error}</p>}
    </div>
  );
};

interface ICardImageCheckbox extends ComponentProps<typeof Root> {
  children: ReactNode;
  error?: string;
  indicatorClassName?: string;
  label?: string;
}

export const CardImageCheckbox = ({
  className,
  error,
  children,
  disabled,
  indicatorClassName,
  label,
  ...props
}: ICardImageCheckbox) => {
  return (
    <div className="inline-flex flex-col">
      <Root
        data-slot="checkbox"
        data-variant="card-image"
        className={mergeClasses(
          'group relative flex flex-col items-center justify-center gap-8 rounded-2xl border transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 overflow-hidden',
          // Default
          'bg-neutral-0 border-neutral-4',
          // Selected
          'data-[state=checked]:border-primary-base',
          // Disabled
          disabled && 'cursor-not-allowed bg-neutral-2 border-neutral-4',
          // Disabled + Selected
          disabled && 'data-[state=checked]:!bg-neutral-2 data-[state=checked]:!border-neutral-4',
          // Error
          error && 'border-semantic-error-base',
          className,
        )}
        {...props}
        disabled={disabled}
      >
        <div className={mergeClasses('relative', disabled && 'opacity-60')}>{children}</div>

        <div className="flex items-center gap-2">
          <span
            data-slot="checkbox-indicator"
            className={mergeClasses(
              'relative size-6 rounded-full border bg-neutral-0 border-neutral-4 grid place-content-center text-white transition-colors',
              // Selected
              'group-data-[state=checked]:bg-primary-base group-data-[state=checked]:border-primary-base',
              // Disabled
              disabled &&
                'group-data-[state=unchecked]:bg-neutral-2 group-data-[state=unchecked]:border-neutral-4 group-data-[state=checked]:bg-neutral-6 group-data-[state=checked]:border-neutral-6',
              // Disabled + Selected
              disabled &&
                'group-data-[state=checked]:!bg-neutral-5 group-data-[state=checked]:!border-neutral-5 group-data-[state=checked]:!text-neutral-0',
              indicatorClassName,
            )}
          >
            <Indicator className="grid place-content-center text-current transition-none">
              <SvgIcon
                component={props.checked === 'indeterminate' ? MinusIcon : CheckIcon}
                className="size-5"
              />
            </Indicator>
          </span>

          {label && (
            <span
              className={mergeClasses(
                'text-h6 !font-bold',
                disabled ? 'text-neutral-7' : 'text-neutral-9',
              )}
            >
              {label}
            </span>
          )}
        </div>
      </Root>
      {error && <p className="text-xs leading-5 text-semantic-error-base mt-1">{error}</p>}
    </div>
  );
};
